import { createContext, useContext, useState, ReactNode, useEffect } from "react";
import { Contact } from "@shared/types/UserAccountTypes.interface";
import { useGlobalSettings } from "@shared/hooks/useGlobalSettings";
import { useMutation, useQuery } from "@tanstack/react-query";
import { fetchContacts, updateContacts } from "@shared/helpers/apis/contactHelper";

interface ContactContextProps {
    contacts: Contact[] | null;
    refetchContacts: () => void;
    updateContacts: (contacts: Contact[]) => void;
    blank: Contact;
    primaryContact?: Contact;
}

const ContactContext = createContext<ContactContextProps | undefined>(undefined);

export const ContactProvider = ({ children }: { children: ReactNode }) => {
    const [contacts, setContacts] = useState<Contact[]>([]);
    const [blankContact] = useState<Contact>({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        isPrimary: false
    });

    const { customer } = useGlobalSettings();
    const customerNumber = customer?.customerNumber;
    const { data: fetchedContacts, refetch: refetchContacts } = useQuery({
        queryKey: ["contacts", customerNumber],
        queryFn: () => fetchContacts(customerNumber),
        enabled: !!customerNumber,
    });

    useEffect(() => {
        if (fetchedContacts) {
            setContacts(fetchedContacts.contacts);
        }
    }, [fetchedContacts]);

    const contactsMutation = useMutation({
        mutationFn: (contacts: Contact[]) => updateContacts(customerNumber, contacts),
        onSuccess: () => {
            refetchContacts();
        },
    });

    return (
        <ContactContext.Provider
            value={{
                contacts: contacts,
                blank: blankContact,
                primaryContact: contacts.find(contact => contact.isPrimary),
                refetchContacts,
                updateContacts: (contacts: Contact[]) => {
                    contactsMutation.mutateAsync(contacts)
                }
            }}
        >
            {children}
        </ContactContext.Provider>
    );
};

export const useContact = () => {
    const context = useContext(ContactContext);
    if (!context) {
        throw new Error("useContact must be used within a ContactProvider");
    }
    return context;
};
